import { FC } from 'react';
import { FormattedMessage } from 'dibs-react-intl';
import { graphql, useFragment } from 'react-relay';
import classnames from 'classnames';
import { BarHeader } from 'dibs-elements/exports/BarHeader';
import { filterFalsy } from 'dibs-ts-utils/exports/filterFalsy';
import { SharedItemTile } from '../../shared/SharedItemTile/SharedItemTile';
import { QuickViewProvider } from '../../global/QuickViewProvider/QuickViewProvider';
import { SbSharedItemTrackingProvider } from '../SbSharedItemTrackingProvider/SbSharedItemTrackingProvider';
import { useSbSelector } from '../../reducers/useSbSelector';
import { useServerVarsContext } from '../../global/ServerVarsContext/ServerVarsContext';
import { useShowItemLocationBadge } from '../../shared/hooks/useShowItemLocationBadge';

import { SbSharedRelatedItems_itemSearch$key } from './__generated__/SbSharedRelatedItems_itemSearch.graphql';
import { SbSharedRelatedItems_viewer$key } from './__generated__/SbSharedRelatedItems_viewer.graphql';

import styles from './SbSharedRelatedItems.scss';
import dibsCss from 'dibs-css';

type Props = {
    viewer: SbSharedRelatedItems_viewer$key;
    itemSearch: SbSharedRelatedItems_itemSearch$key;
};

export const SbSharedRelatedItems: FC<Props> = ({
    viewer: viewerRef,
    itemSearch: itemSearchRef,
}) => {
    const viewer = useFragment(
        graphql`
            fragment SbSharedRelatedItems_viewer on Viewer {
                ...SharedItemTile_viewer
                ...SbSharedItemTrackingProvider_viewer
            }
        `,
        viewerRef
    );
    const itemSearch = useFragment(
        graphql`
            fragment SbSharedRelatedItems_itemSearch on ItemSearchQueryConnection {
                ...SharedItemTile_itemSearch
                ...SbSharedItemTrackingProvider_itemSearch
                relatedItems: youMayAlsoLike(first: 12) {
                    edges {
                        node {
                            item {
                                serviceId
                                ...SharedItemTile_item
                                    @arguments(
                                        isTrade: $isTrade
                                        userCountryCode: $userCountryCode
                                        priceBookName: $priceBookName
                                        showSeller: $showSeller
                                    )
                                ...SbSharedItemTrackingProvider_item
                                ...shippingCostHelpers_item
                            }
                        }
                    }
                }
            }
        `,
        itemSearchRef
    );
    const { isMobile } = useServerVarsContext();
    const isClient = useSbSelector(store => store.relayVariables.variables.isClient);
    const showItemLocationBadge = useShowItemLocationBadge();
    const relatedItems = (itemSearch?.relatedItems?.edges || [])
        .map(item => item?.node?.item || null)
        .filter(filterFalsy);

    const title = (
        <FormattedMessage id="sb.SbSharedRelatedItems.title" defaultMessage="Related Items" />
    );

    if (relatedItems.length > 0) {
        return (
            <SbSharedItemTrackingProvider
                viewer={viewer}
                itemSearch={itemSearch}
                items={relatedItems}
                pageType="related"
            >
                {({ fireItemImpressionTracking, fireItemClickTracking }) => (
                    <>
                        <BarHeader title={title} />
                        <QuickViewProvider>
                            {({ quickView }) => {
                                return (
                                    <div
                                        className={classnames(
                                            dibsCss.flex,
                                            dibsCss.flexWrap,
                                            dibsCss.mbSmall,
                                            { [styles.container]: isMobile }
                                        )}
                                        data-tn="related-items"
                                    >
                                        {relatedItems.map((relatedItem, index) => {
                                            if (relatedItem) {
                                                return (
                                                    <SharedItemTile
                                                        isClient={isClient}
                                                        key={index}
                                                        index={index}
                                                        item={relatedItem}
                                                        showSellerName={false}
                                                        showItemLocationBadge={
                                                            showItemLocationBadge
                                                        }
                                                        viewer={viewer}
                                                        quickView={quickView}
                                                        itemSearch={itemSearch}
                                                        isMobile={isMobile}
                                                        onContentVisible={() => {
                                                            fireItemImpressionTracking({
                                                                itemId: relatedItem?.serviceId,
                                                                index,
                                                            });
                                                        }}
                                                        onClick={() =>
                                                            fireItemClickTracking({
                                                                itemId: relatedItem?.serviceId,
                                                                index,
                                                            })
                                                        }
                                                    />
                                                );
                                            } else {
                                                return null;
                                            }
                                        })}
                                    </div>
                                );
                            }}
                        </QuickViewProvider>
                    </>
                )}
            </SbSharedItemTrackingProvider>
        );
    }
    return null;
};
